import React from 'react';
import { Container } from '@charlietango/ui';
import { rem } from '../../styles/rem';

import { useUser } from '../../api/hooks/useUser';
import HowToSection from '../../components/HowToSection/HowToSection';
import AccordionViewModel from '../../view-models/AccordionViewModel';
import FrontPageInitiateDeclarationViewModel from './types/FrontPageInitiateDeclarationViewModel';
import Hero from '../../components/Hero/Hero';
import Steps from '../../components/Steps/Steps';
import { StepViewModel } from '../../components/Steps/types/StepViewModel';
import { HeroViewModel } from '../../components/Hero/types/HeroViewModel';
import { resolveStepsContent } from '../../components/Steps/resolveStepsContent';

export type FrontPageProps = {
  initiateDeclaration?: FrontPageInitiateDeclarationViewModel;
  howTo?: AccordionViewModel;
  stepsIntro?: string;
  stepsContent?: StepViewModel[];
  heroProps?: HeroViewModel;
};

function FrontPage({
  heroProps,
  initiateDeclaration,
  howTo,
  stepsIntro,
  stepsContent,
}: FrontPageProps) {
  const { user, userReady } = useUser();
  const resolvedStepsContent = resolveStepsContent(stepsContent).map(
    (step) => ({
      ...step,
      image: step.image ?? heroProps?.image,
    }),
  );

  return (
    <>
      <div
        sx={{
          borderBottom: '1px solid rgba(31, 111, 111, 0.20)',
        }}
      >
        <Container
          sx={{
            pt: [rem(32), rem(80), rem(120)],
            pb: [rem(80), rem(80), rem(120)],
            px: [0, rem(20), rem(20)],
            maxWidth: ['none', 'none', rem(992)],
          }}
        >
          <Hero
            initiateDeclaration={initiateDeclaration}
            user={user}
            userReady={userReady}
            heroProps={heroProps}
          />
        </Container>
      </div>

      <div
        sx={{
          '@media (min-width: 62em)': {
            borderBottom: '1px solid rgba(31, 111, 111, 0.20)',
          },
        }}
      >
        <Container sx={{ mx: 'auto', px: [0, null, 0] }}>
          <div
            sx={{
              '& > section': {
                mt: [rem(80), rem(80), rem(120)],
                mb: [0, 0, rem(120)],
              },
            }}
          >
            <Steps intro={stepsIntro} steps={resolvedStepsContent} />
          </div>
        </Container>
      </div>

      <Container
        sx={{
          mx: 'auto',
          mb: [rem(80), rem(80), rem(120)],
          px: [0, null, 0],
        }}
      >
        {howTo && (
          <div
            sx={{
              mt: [rem(80), rem(80), rem(120)],
              mb: [rem(56), null, rem(56)],
              px: [rem(20), rem(20), rem(20)],
            }}
          >
            <HowToSection title={howTo.title} items={howTo.items} />
          </div>
        )}
      </Container>
    </>
  );
}

FrontPage.displayName = 'FrontPage';

export default FrontPage;
